import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { APP_ROUTES } from "@/config/routes.config";
import useAuthUser from "@/hooks/useAuthUser";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { PATH_HOME } from "@/routes/paths";

const AppSidebar = () => {
  const { pathname } = useLocation();
  const { user, hasPermission } = useAuthUser();
  const { open, state, isMobile, setOpenMobile } = useSidebar();
  const isCollapsed = state === "collapsed";

  // `public` routes are always visible. Legacy admins (user.isAdmin) see every route.
  // Otherwise the route is shown only when the user holds at least one of its
  // `requiredPermissions`.
  const isRouteVisible = (route: (typeof APP_ROUTES)[number]) => {
    if (route.public || user.isAdmin) return true;
    return route.requiredPermissions?.length ? hasPermission(route.requiredPermissions) : false;
  };

  const visibleMainRoutes = APP_ROUTES.filter((route) => route.section === "main" && isRouteVisible(route));
  const footerRoutes = APP_ROUTES.filter((route) => route.section === "footer" && isRouteVisible(route));

  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [pathname, isMobile, setOpenMobile]);

  return (
    <Sidebar collapsible="icon">
      {/* Floating collapse/expand toggle - half in, half out of sidebar */}
      <div className="hidden md:block absolute top-20 -right-[15px] z-50">
        <SidebarTrigger />
      </div>

      <SidebarHeader className="p-4">
        <div className="flex justify-between items-center w-full">
          <Link to={PATH_HOME} className="flex gap-3 items-center md:m-auto cursor-pointer">
            <div data-state={state}>
              {state === "expanded" ? (
                <img src="/logos/ironpipeline.svg" className="h-6 lg:h-7 xl:h-8 transition duration-400 ease-linear" />
              ) : (
                <img src="/logos/ironpipeline-mobile.png" className="w-8 transition duration-400 ease-linear delay-150" />
              )}
            </div>
          </Link>
          {isMobile && <X className="cursor-pointer" onClick={() => setOpenMobile(false)} />}
        </div>
      </SidebarHeader>
      <div className="relative flex h-full w-full flex-col scroll-custom">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel
            className={cn("text-white font-semibold text-sm", { "mx-auto": isCollapsed && !isMobile })}
          >
            MENU
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleMainRoutes.map((item) => {
                const isActive = item.path === pathname;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      className="mx-auto"
                      tooltip={item.title}
                      size="lg"
                      asChild
                      isActive={isActive}
                    >
                      <Link
                        to={item.path}
                        className={cn(
                          "text-white/50 hover:text-white transition-colors",
                          isActive && "!text-white bg-gradient-to-br from-dashboard-blue/40 to-dashboard-blue/[0.04] rounded-[5px]"
                        )}
                      >
                        {item.icon && <item.icon className={cn("w-5 h-5", { "mx-auto": !open && !isMobile })} />}
                        {(open || isMobile) && <span>{item.title}</span>}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarGroupLabel
          className={cn("text-white font-semibold text-sm", { "mx-auto": isCollapsed && !isMobile })}
        >
          OTHERS
        </SidebarGroupLabel>
        <SidebarMenu>
          {footerRoutes.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton className="mx-auto" tooltip={item.title} size="lg" asChild>
                <Link to={item.path} className="text-white text-opacity-50">
                  {item.icon && <item.icon className={cn("w-6 h-6", { "mx-auto": !open && !isMobile })} />}
                  {(open || isMobile) && <span>{item.title}</span>}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarFooter>
      </div>
    </Sidebar>
  );
};

export default AppSidebar;
